import React, { useState } from 'react';
import { Button, LoadingShimmer, colors } from '@podiumhq/podium-ui';
import { IconCheck } from '@podiumhq/icons';
import { useQuery, useMutation } from '@apollo/react-hooks';
import cookies from 'js-cookie';
import { Redirect } from 'react-router-dom';

import {
  Text,
  Logo,
  ConnectWrapper,
  ImageWrapper,
  Dots,
  TextLoading,
  ImagePlaceholder,
  ScopesWrapper,
  ScopesTitle,
  Scope
} from './styles';
import logo from '../../images/Logo.png';
import dots from '../../images/Dots.png';
import connectLogo from '../../images/ConnectLogo.png';
import InvalidLoginBanner from '../InvalidLoginBanner';
import { OAUTH_AUTHORIZATION, OAUTH_DENY } from '../../graphql/mutations';
import { OAUTH_PREAUTHORIZATION } from '../../graphql/queries';
import paths from '../../lib/paths';
import { KAZAAM_HOST } from '../../lib/env';

export default function OAuthAuthorize() {
  const [grantError, setGrantError] = useState(null);
  const [renderPage, setRenderPage] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get('client_id');
  const redirectUri = urlParams.get('redirect_uri');
  const scope = urlParams.get('scope');
  const idToken = cookies.get('idToken');
  const stateParam = urlParams.get('state');

  const missingData = !idToken || !clientId || !redirectUri;

  const {
    loading: preAuthLoading,
    error: preAuthError,
    data: preAuthData
  } = useQuery(OAUTH_PREAUTHORIZATION, {
    skip: missingData,
    variables: {
      idToken,
      input: {
        clientId,
        redirectUri,
        responseType: 'CODE',
        scope,
        state: stateParam
      }
    },
    onCompleted() {
      setRenderPage(true);
    }
  });
  const scopes = preAuthData?.oauthPreauthorize?.scopes || [];
  const appName = preAuthData?.oauthPreauthorize?.client?.name;
  const appLogo = preAuthData?.oauthPreauthorize?.client?.['logo_uri'];

  const preAuthRedirectUri = preAuthData?.oauthPreauthorize?.redirectUri;

  if (preAuthRedirectUri) window.location.replace(preAuthRedirectUri);

  const [authorizeOAuth, { loading }] = useMutation(OAUTH_AUTHORIZATION, {
    onError() {
      setGrantError(true);
    },
    onCompleted(data) {
      const {
        oauthAuthorize: { redirectUri: redirectTo }
      } = data;

      window.open(redirectTo, '_blank');
      window.location.replace(KAZAAM_HOST);
    }
  });

  const [denyOAuth, { loading: denyLoading }] = useMutation(OAUTH_DENY, {
    onCompleted(data) {
      const {
        oauthDeny: { redirectUri: redirectTo }
      } = data;

      window.open(redirectTo, '_blank');
      window.location.replace(KAZAAM_HOST);
    }
  });

  const authorizeAccess = () => {
    setGrantError(false);

    authorizeOAuth({
      variables: {
        idToken,
        input: {
          clientId,
          redirectUri,
          responseType: 'CODE',
          scope,
          state: stateParam
        }
      }
    });
  };

  const denyAccess = () => {
    setGrantError(false);

    denyOAuth({
      variables: {
        idToken,
        input: {
          clientId,
          redirectUri,
          responseType: 'CODE',
          scope,
          state: stateParam
        }
      }
    });
  };

  const redirectToLogin = preAuthError || missingData;
  const showLoading = preAuthLoading || preAuthError || !renderPage;

  return (
    <div>
      {redirectToLogin && <Redirect to={paths.root} />}
      <Logo>
        <img src={logo} height="28px" alt="Podium Logo" />
      </Logo>
      <ConnectWrapper>
        {showLoading ? (
          <ImagePlaceholder />
        ) : (
          <ImageWrapper src={appLogo} alt={appName} />
        )}
        <Dots>
          <img src={dots} width="65%" alt="connect dots" />
        </Dots>
        {showLoading ? (
          <ImagePlaceholder />
        ) : (
          <ImageWrapper src={connectLogo} alt="Podium Connect Logo" />
        )}
      </ConnectWrapper>
      <Text>
        {showLoading ? (
          <TextLoading>
            <LoadingShimmer width={400} height={16} />
            <LoadingShimmer width={200} height={16} />
          </TextLoading>
        ) : (
          `Allow ${appName} access to the following data`
        )}
      </Text>
      <ScopesWrapper>
        {showLoading ? (
          <>
            <LoadingShimmer width={150} height={16} />
            <div style={{ height: '16px' }} />
            <LoadingShimmer width={350} height={16} />
            <div style={{ height: '16px' }} />
            <LoadingShimmer width={350} height={16} />
          </>
        ) : (
          <>
            <ScopesTitle>{appName} would like to:</ScopesTitle>
            {scopes.map(({ name }) => (
              <Scope key={name}>
                <IconCheck color={colors.podiumBrand} />
                <span>{name}</span>
              </Scope>
            ))}
          </>
        )}
      </ScopesWrapper>
      <InvalidLoginBanner
        show={grantError}
        message="Something went wrong. Please try again."
      />
      <Button
        fullwidth
        size="large"
        onClick={authorizeAccess}
        isLoading={loading}
        disabled={showLoading}
        loadingText="Granting access..."
        style={{ marginBottom: '24px' }}
      >
        Grant access
      </Button>
      <Button
        type="text"
        disabled={showLoading}
        isLoading={denyLoading}
        loadingText="Denying..."
        fullwidth
        size="large"
        onClick={denyAccess}
      >
        Deny
      </Button>
    </div>
  );
}
